// ** react imports
import React from 'react';

// ** context imports
import { QuizProvider, useQuiz } from '../../../contexts/QuizContext';
import { useTaxFileDetails } from '../../../contexts/TaxFileDetailsContext';

// ** components imports
import Loading from '../../common/Loading';
import QuizHeader from './quiz/QuizHeader';
import NoQuizData from './quiz/NoQuizData';
import QuestionList from './quiz/QuestionList';
import SectionNavigation from './quiz/SectionNavigation';
import TaxFileHistory from './quiz/TaxFileHistory';

function QuizResponseContent() {
  const {
    isHistoryMode,
    quizSections,
    isEditMode,
    handleEditQuiz,
    isUpdateSectionAnswerByAdminLoading,
    isUpdateSectionAnswerByAdminError,
    updateSectionAnswerByAdminError,
    errors,
    saveReason,
    setSaveReason,
    activeSectionData,
    toggleHistoryMode,
  } = useQuiz();

  const {
    isGetSectionsWithQuestionsLoading,
    isGetQuizResponseByTaxFileIdFetching,
  } = useTaxFileDetails();

  const formattedSectionErrors = React.useMemo(() => {
    const out = [];
    if (!errors || !activeSectionData?.questions) return out;
    const qById = new Map(
      (activeSectionData.questions || []).map(q => [q.questionId, q])
    );

    const getSubFields = q =>
      Array.isArray(q?.where?.fields)
        ? q.where.fields
        : Array.isArray(q?.fields)
          ? q.fields
          : [];

    const getSubFieldLabel = (q, subId) => {
      const sf = getSubFields(q).find(f => f?.questionId === subId);
      return sf?.questionText || subId;
    };

    Object.entries(errors).forEach(([qid, err]) => {
      const q = qById.get(qid);
      if (!q) return;

      // If this is a top-level field error (e.g., required/min for the array itself)
      if (err && typeof err === 'object' && 'message' in err && err.message) {
        out.push({
          key: `q-${qid}`,
          text: `${q.questionText || qid}: ${err.message}`,
        });
        return;
      }

      // Dynamic group: array of per-item error objects
      if (Array.isArray(err)) {
        err.forEach((itemErr, idx) => {
          if (!itemErr || typeof itemErr !== 'object') return; // skip nulls/others
          Object.entries(itemErr).forEach(([subId, subErr]) => {
            if (!subErr || typeof subErr !== 'object') return;
            const msg =
              subErr.message ||
              subErr?.types?.message ||
              subErr?.types?.default;
            if (!msg) return;
            const subLabel = getSubFieldLabel(q, subId);
            out.push({
              key: `q-${qid}-${idx}-${subId}`,
              text: `${q.questionText || qid} · Item #${idx + 1} · ${subLabel}: ${msg}`,
            });
          });
        });
        return;
      }

      // Nested object (non-array) errors: iterate own keys
      if (err && typeof err === 'object') {
        Object.entries(err).forEach(([subId, subErr]) => {
          if (!subErr || typeof subErr !== 'object') return;
          const msg =
            subErr.message || subErr?.types?.message || subErr?.types?.default;
          if (!msg) return;
          const subLabel = getSubFieldLabel(q, subId);
          out.push({
            key: `q-${qid}-${subId}`,
            text: `${q.questionText || qid} · ${subLabel}: ${msg}`,
          });
        });
      }
    });

    return out;
  }, [errors, activeSectionData]);

  return (
    <section className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <QuizHeader
        isHistoryMode={isHistoryMode}
        toggleHistoryMode={toggleHistoryMode}
        isSaving={isUpdateSectionAnswerByAdminLoading}
      />

      <div className="relative">
        {isGetSectionsWithQuestionsLoading ||
        isGetQuizResponseByTaxFileIdFetching ? (
          <div className="min-h-[calc(70vh-100px)] bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
            <div className="flex items-center justify-center gap-3 text-gray-600 dark:text-gray-300">
              <Loading size="medium" />
            </div>
          </div>
        ) : !quizSections || quizSections.length === 0 ? (
          <NoQuizData handleEditQuiz={handleEditQuiz} />
        ) : (
          <div className="p-4 sm:p-6">
            {isEditMode && (
              <div className="mb-6 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Reason for Changes (Optional)
                </label>
                <textarea
                  value={saveReason}
                  onChange={e => setSaveReason(e.target.value)}
                  placeholder="Enter reason for making these changes..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-violet-50 focus:border-violet-500 dark:bg-gray-800 dark:text-white"
                  rows={3}
                />
              </div>
            )}

            {isUpdateSectionAnswerByAdminError && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-red-700 dark:text-red-300">
                  Error saving changes:{' '}
                  {updateSectionAnswerByAdminError?.data?.message ||
                    'Failed to save changes'}
                </p>
              </div>
            )}

            {formattedSectionErrors.length > 0 && (
              <div className="mb-6 p-4 bg-red-50 dark:bg-red-100/5 rounded-lg">
                <p className="text-red-700 dark:text-red-30 font-medium mb-2">
                  Please fix the following errors:
                </p>
                <ul className="list-disc list-inside text-red-700 dark:text-red-300">
                  {formattedSectionErrors.map(e => (
                    <li key={e.key}>{e.text}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="relative grid grid-cols-1 md:grid-cols-4 gap-4 sm:gap-6">
              {isHistoryMode ? (
                <div className="col-span-4">
                  <TaxFileHistory />
                </div>
              ) : (
                <>
                  <SectionNavigation />

                  <div className="col-span-3">
                    <QuestionList />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const QuizResponseSection = () => {
  return (
    <QuizProvider>
      <QuizResponseContent />
    </QuizProvider>
  );
};

export default QuizResponseSection;
